/**
 * Utility functions for User Migration
 */

import { UNIT_HIERARCHY_UNASSIGNED } from './user-migration.constants';

export interface ParsedUnitHierarchy {
    area: string;
    distrito: string;
    comunidad: string;
}

/**
 * Parse unit hierarchy string into components (area, distrito, comunidad)
 */
export function parseUnitHierarchy(hierarchy: string): ParsedUnitHierarchy {
    if (!hierarchy || UNIT_HIERARCHY_UNASSIGNED.includes(hierarchy)) {
        return { area: '', distrito: '', comunidad: '' };
    }

    const parts = hierarchy.split(' - ').map(p => p.trim());
    let area = '';
    let distrito = '';
    let comunidad = '';

    if (parts.length === 3) {
        comunidad = formatUnitName(parts[0]);
        distrito = formatUnitName(parts[1]);
        area = formatUnitName(parts[2]);
    } else if (parts.length === 2) {
        distrito = formatUnitName(parts[0]);
        area = formatUnitName(parts[1]);
    } else if (parts.length === 1) {
        area = formatUnitName(parts[0]);
    }

    return { area, distrito, comunidad };
}

/**
 * Extract ID from unit string in format "Name (ID:XXX)" or "Name (XXX)"
 */
export function extractUnitId(unit: string): string {
    if (!unit) return '';
    
    // Try format with ID: label first
    const idMatch = unit.match(/\(ID:([^)]+)\)/);
    if (idMatch) return idMatch[1].trim();
    
    // Try format with just parentheses (ID without label)
    const parenMatch = unit.match(/\(([^)]+)\)$/);
    if (parenMatch) {
        const content = parenMatch[1].trim();
        // Return if it looks like an ID (numeric or alphanumeric code)
        if (/^\d+$/.test(content) || /^[A-Z]+\d+$/.test(content)) {
            return content;
        }
    }
    
    return '';
}

/**
 * Format unit name by removing ID and labels
 */
export function formatUnitName(unit: string): string {
    if (!unit) return '';

    // Extract name without ID in format "Name (ID:XXX)"
    const match = unit.match(/^(.+?)\s*\(ID:([^)]+)\)$/);
    let name = match ? match[1].trim() : unit;

    // Remove common prefixes
    name = name.replace(/^Distrito\s+/, '')
        .replace(/^Iglesia\s+/, '')
        .replace(/^Área\s+/, '');

    return name;
}

/**
 * Sort items by ID extracted from parentheses format "Name (ID:XXX)"
 */
export function sortByIdInParentheses(a: string, b: string): number {
    const idRegex = /\(ID:([^)]+)\)/;
    const matchA = a.match(idRegex);
    const matchB = b.match(idRegex);

    if (matchA && matchB) {
        const idA = matchA[1].trim();
        const idB = matchB[1].trim();

        const numA = parseFloat(idA);
        const numB = parseFloat(idB);

        if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
        }

        return idA.localeCompare(idB);
    }

    return a.localeCompare(b);
}

/**
 * Normalize and trim search text
 */
export function normalizeSearchText(searchText?: string): string {
    return (searchText ?? '').trim().toLowerCase();
}

/**
 * Filter options by search text
 */
export function filterOptions(options: string[], searchText?: string): string[] {
    const normalizedTerm = normalizeSearchText(searchText);
    if (!normalizedTerm) return options;
    return options.filter(option => option.toLowerCase().includes(normalizedTerm));
}

